import React, { useCallback } from 'react';

import Button from '@atlaskit/button/new';
import { useFlags } from '@atlaskit/flag';
import SuccessIcon from '@atlaskit/icon/glyph/check-circle';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import TextArea from '@atlaskit/textarea';
import { G300, R300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

interface FeedbackCollectorProps {
	onClose(): void;
	onSubmitFeedback: (
		event: any,
		feedbackValue: string,
		feedbackSuccess: () => void,
		feedbackFailure: () => void,
	) => void;
	testId?: string;
	updateModalVal: (val: string) => void;
}

/**
 * __Feedback collector__
 */
const FeedbackCollector = ({
	onClose,
	onSubmitFeedback,
	updateModalVal,
	testId,
}: FeedbackCollectorProps) => {
	const [feedbackVal, setFeedbackVal] = React.useState<string>('');

	const { showFlag } = useFlags();

	const showSuccessFeedbackFlag = useCallback(() => {
		showFlag({
			icon: (
				<SuccessIcon label="Success" primaryColor={token('color.background.success.bold', G300)} />
			),
			description: 'Your feedback has been submitted.',
			isAutoDismiss: true,
			title: 'Thank you!',
		});
	}, [showFlag]);

	const showFailureFeedbackFlag = useCallback(() => {
		showFlag({
			icon: <CrossCircleIcon label="Failure" primaryColor={token('color.icon.danger', R300)} />,
			description: 'Your feedback failed to submit.',
			isAutoDismiss: true,
			title: 'Please try again.',
		});
	}, [showFlag]);

	const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setFeedbackVal(e.target.value);
	};

	return (
		<Modal testId={testId}>
			<form>
				<ModalHeader>
					<ModalTitle>Thanks for your feedback</ModalTitle>
				</ModalHeader>
				<ModalBody>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */}
					<div style={{ marginBottom: '5px' }}>You can tell us more below (optional)</div>
					<TextArea
						value={feedbackVal}
						onChange={onChange}
						placeholder="Share your feedback here"
					/>
				</ModalBody>
				<ModalFooter>
					<Button
						onClick={(e) => {
							updateModalVal(feedbackVal);
							onClose();
							onSubmitFeedback(e, feedbackVal, showSuccessFeedbackFlag, showFailureFeedbackFlag);
						}}
					>
						Close
					</Button>
					<Button
						testId="submit-feedback-button"
						appearance="primary"
						onClick={(e) => {
							updateModalVal(feedbackVal);
							onClose();
							onSubmitFeedback(e, feedbackVal, showSuccessFeedbackFlag, showFailureFeedbackFlag);
						}}
					>
						Send feedback
					</Button>
				</ModalFooter>
			</form>
		</Modal>
	);
};

export default FeedbackCollector;
